/**
 * The selected role's CRM rights, and the one button that writes them.
 *
 * Checkboxes, not a grid of pills and not a table. Paragraph 15 of the design
 * notes draws the line at what the shape is FOR: a pill row is a record you open
 * or act on, a table is figures you compare across rows. This is neither - it is
 * one role's form, and the same right on a second role is not on this screen to
 * be compared with. A `<table>` here would draw rules between a label and its
 * own checkbox and call it a matrix.
 */
import { Loader2, Lock, Save, SlidersHorizontal } from "lucide-react";
import { Checkbox, cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { Btn, Card, CardHead, StatusPill } from "../../../ui";
import { permSlug, roleLabel, type Permission, type RoleDetail } from "./types";

export function CrmPermissionsCard({
  detail,
  locale,
  perms,
  grants,
  inherited,
  grantedCount,
  saving,
  dirty,
  saved,
  onToggle,
  onToggleAll,
  onSave,
}: {
  detail: RoleDetail;
  locale: string;
  perms: Permission[];
  /** The CRM keys granted on the role itself, as the operator has edited them. */
  grants: Set<string>;
  /** Granted by a parent role: shown ticked, never editable here. */
  inherited: Set<string>;
  grantedCount: number;
  saving: boolean;
  dirty: boolean;
  saved: boolean;
  onToggle: (key: string) => void;
  onToggleAll: (allOn: boolean) => void;
  onSave: () => void;
}) {
  const t = useT();
  const isSuper = detail.isSuperadmin;
  const total = perms.length;
  const allOn = perms.every((p) => grants.has(p.key) || inherited.has(p.key));

  return (
    <Card>
      <CardHead
        title={
          <span className="flex items-center gap-2.5">
            <SlidersHorizontal className="size-[17px] shrink-0" style={{ color: "var(--color-primary)" }} />
            {t("roles.crmPermsTitle")}
            <span className="crm-mut crm-tnum text-[13px] font-[550]">
              {grantedCount}/{total}
            </span>
          </span>
        }
        sub={`${roleLabel(detail, locale)} · ${detail.key}`}
        actions={
          !isSuper ? (
            <Btn small onClick={() => onToggleAll(allOn)}>
              {allOn ? t("roles.clearAll") : t("roles.selectAll")}
            </Btn>
          ) : null
        }
      />

      {/* `crm-card` is a surface with no padding of its own. */}
      <div className="space-y-4 px-[26px] pb-6 pt-3.5">
        {isSuper ? (
          <p
            className="rounded-[20px] px-4 py-3 text-[12.75px]"
            style={{
              background: "color-mix(in oklab, var(--color-warning) 14%, var(--crm-surface))",
              color: "color-mix(in oklab, var(--color-warning) 52%, var(--color-foreground))",
            }}
          >
            {t("roles.superadminLocked")}
          </p>
        ) : null}

        <div className="grid gap-x-6 gap-y-1 md:grid-cols-2">
          {perms.map((p) => {
            const isInherited = inherited.has(p.key);
            const checked = isSuper || grants.has(p.key) || isInherited;
            const locked = isSuper || isInherited;
            return (
              <label
                key={p.key}
                className={cn(
                  "-mx-2.5 flex items-start gap-3 rounded-[16px] px-2.5 py-2",
                  locked ? "cursor-not-allowed opacity-80" : "cursor-pointer hover:bg-[var(--crm-sunk)]",
                )}
              >
                <Checkbox
                  className="mt-0.5"
                  checked={checked}
                  disabled={locked}
                  onCheckedChange={() => onToggle(p.key)}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-[13px] font-[550] leading-tight">
                    {t(`roles.perm.${permSlug(p.key)}`)}
                    {isInherited ? (
                      <Lock className="crm-mut size-3 shrink-0" aria-label={t("roles.inheritedFlag")} />
                    ) : null}
                  </span>
                  {/* The raw key, because it is what the server checks and what
                      anyone comparing this screen with the admin panel reads. */}
                  <span className="crm-mut block font-mono text-[11px] leading-snug">{p.key}</span>
                </span>
              </label>
            );
          })}
        </div>

        {!isSuper ? (
          <div className="flex flex-wrap items-center gap-3 border-t border-[var(--crm-hair)] pt-5">
            <Btn tone="dark" onClick={onSave} disabled={saving || !dirty}>
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              {t("roles.save")}
            </Btn>
            {saved ? <StatusPill tone="ok">{t("roles.saved")}</StatusPill> : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
